import { create } from 'zustand';

export type Period = 'month' | 'next' | 'ytd';
export type YearMode = 'faktisk' | 'budget';
export type OpenMenu = 'year' | 'month' | null;

interface UiState {
  year: number;
  month: number;
  period: Period;
  yearMode: YearMode;
  editNames: boolean;
  sidebarCollapsed: boolean;
  dark: boolean;
  openCats: string[];
  openMenu: OpenMenu;
  toast: string | null;
  setYear(year: number): void;
  setMonth(month: number): void;
  setPeriod(period: Period): void;
  setYearMode(mode: YearMode): void;
  toggleEditNames(): void;
  toggleSidebar(): void;
  toggleDark(): void;
  toggleCat(name: string): void;
  openCat(name: string): void;
  setOpenMenu(menu: OpenMenu): void;
  setToast(msg: string | null): void;
}

const now = new Date();

const DARK_STORAGE_KEY = 'budgetx.dark';

function readDarkPref(): boolean {
  try {
    return window.localStorage.getItem(DARK_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDarkPref(v: boolean): void {
  try {
    window.localStorage.setItem(DARK_STORAGE_KEY, v ? '1' : '0');
  } catch {
    /* localStorage may be blocked */
  }
}

export const useUiStore = create<UiState>((set) => ({
  year: now.getFullYear(),
  month: now.getMonth(),
  period: 'month',
  yearMode: 'faktisk',
  editNames: false,
  sidebarCollapsed: false,
  dark: readDarkPref(),
  openCats: [],
  openMenu: null,
  toast: null,
  setYear: (year) => set({ year, openMenu: null }),
  setMonth: (month) => set({ month, openMenu: null }),
  setPeriod: (period) => set({ period }),
  setYearMode: (yearMode) =>
    set((s) => ({ yearMode, editNames: yearMode === 'budget' ? s.editNames : false })),
  toggleEditNames: () => set((s) => ({ editNames: !s.editNames })),
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  toggleDark: () =>
    set((s) => {
      const next = !s.dark;
      writeDarkPref(next);
      return { dark: next };
    }),
  toggleCat: (name) =>
    set((s) => ({
      openCats: s.openCats.includes(name)
        ? s.openCats.filter((n) => n !== name)
        : [...s.openCats, name],
    })),
  openCat: (name) =>
    set((s) => (s.openCats.includes(name) ? s : { openCats: [...s.openCats, name] })),
  setOpenMenu: (openMenu) => set({ openMenu }),
  setToast: (toast) => set({ toast }),
}));
